// AGENT-A5: API client for the dashboard rebuild.
//
// Hits /api/dashboard/filters and /api/dashboard/report (owned by A2/A3).
// When VITE_USE_MOCK=true, dynamically imports ./mock.ts (owned by A9) so an
// offline `npm run dev` works without the backend running.
//
// AGENT-A9: ./mock.ts exports both prefixed names (mockGetFilters /
// mockPostReport — the spec's canonical entry points) and the bare aliases
// (getFilters / postReport) that loadMock() consumes here. Do not rename the
// bare aliases without updating MockModule below.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type {
  FiltersResponse,
  PeriodsResponse,
  ReportResponse,
  ServerFilterShape,
} from "../types";

const API_PREFIX = "/api/dashboard";

const USE_MOCK =
  typeof import.meta !== "undefined" &&
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_USE_MOCK === "true";

// AGENT-STATIC: Cloudflare-Pages static-bake mode. When VITE_DASH_STATIC=true the
// app loads baked artifacts (/hsq-data/*) and computes filters/report entirely
// in-browser via ./static-compute — no live backend, instant loads.
const USE_STATIC =
  typeof import.meta !== "undefined" &&
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_DASH_STATIC === "true";

interface MockModule {
  getFilters: () => Promise<FiltersResponse>;
  postReport: (state: ServerFilterShape) => Promise<ReportResponse>;
}

async function loadMock(): Promise<MockModule> {
  // Dynamic import so the mock module is tree-shaken in production builds.
  // Wrapped in @vite-ignore so the build doesn't fail if A9 hasn't authored
  // ./mock.ts yet — the runtime branch only fires when VITE_USE_MOCK=true.
  return (await import(/* @vite-ignore */ "./mock")) as unknown as MockModule;
}

interface StaticModule {
  getFilters: () => Promise<FiltersResponse>;
  getPeriods: () => Promise<PeriodsResponse>;
  postReport: (state: ServerFilterShape) => Promise<ReportResponse>;
}
async function loadStatic(): Promise<StaticModule> {
  return (await import("./static-compute")) as unknown as StaticModule;
}

export async function getFilters(signal?: AbortSignal): Promise<FiltersResponse> {
  if (USE_STATIC) {
    return (await loadStatic()).getFilters();
  }
  if (USE_MOCK) {
    const m = await loadMock();
    return m.getFilters();
  }
  const res = await fetch(`${API_PREFIX}/filters`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal,
  });
  if (!res.ok) {
    throw new Error(`getFilters failed: ${res.status}`);
  }
  return (await res.json()) as FiltersResponse;
}

/* ------------------------------------------------------------------------- *
 * AGENT-A7: GET /api/dashboard/periods — fiscal-year dropdown source.
 *
 * Backend owner: Agent 2. While that endpoint is offline / pre-merge, we
 * keep the contract aligned with the spec:
 *
 *   { fy: string[],  defaultFy: string }
 *
 * The fetch is cached for 5 minutes; window-focus refetches are disabled
 * because the period list is essentially static within a session.
 * ------------------------------------------------------------------------- */

const PERIODS_FALLBACK: PeriodsResponse = {
  fy: ["FY26", "FY25", "FY24", "FY23", "FY22"],
  defaultFy: "FY26",
};

export async function getPeriods(signal?: AbortSignal): Promise<PeriodsResponse> {
  if (USE_STATIC) {
    return (await loadStatic()).getPeriods();
  }
  if (USE_MOCK) {
    // Mock module may not implement /periods yet; fall back gracefully.
    try {
      const m = (await loadMock()) as unknown as {
        getPeriods?: () => Promise<PeriodsResponse>;
      };
      if (m.getPeriods) return await m.getPeriods();
    } catch {
      // ignore — fall through to the offline fallback shape.
    }
    return PERIODS_FALLBACK;
  }
  try {
    const res = await fetch(`${API_PREFIX}/periods`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal,
    });
    if (!res.ok) {
      // Endpoint not deployed yet (Agent 2 owns it) — degrade to the offline
      // fallback so the UI keeps working in dev.
      return PERIODS_FALLBACK;
    }
    return (await res.json()) as PeriodsResponse;
  } catch {
    // Network error or aborted; degrade.
    return PERIODS_FALLBACK;
  }
}

/** React-query hook for the periods endpoint. 5-minute cache, no window-focus
 *  refetch — the FY list doesn't change inside a session. */
export function usePeriods(): UseQueryResult<PeriodsResponse> {
  return useQuery<PeriodsResponse>({
    queryKey: ["dashboard-periods"],
    queryFn: ({ signal }) => getPeriods(signal),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export async function postReport(
  state: ServerFilterShape,
  signal?: AbortSignal,
): Promise<ReportResponse> {
  if (USE_STATIC) {
    return (await loadStatic()).postReport(state);
  }
  if (USE_MOCK) {
    const m = await loadMock();
    return m.postReport(state);
  }
  const res = await fetch(`${API_PREFIX}/report`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(state),
    credentials: "same-origin",
    signal,
  });
  if (!res.ok) {
    throw new Error(`postReport failed: ${res.status}`);
  }
  return (await res.json()) as ReportResponse;
}
